import React, { useMemo, useState } from 'react';
import { Bell, Play, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Slider } from '../../../components/ui/slider';
import { Switch } from '../../../components/ui/switch';
import {
  playReminderSound,
  REMINDER_SOUND_OPTIONS,
} from '../services/taskReminderAudio';

const PRESET_OFFSETS = [
  [0, 'At due time'],
  [5, '5 minutes before'],
  [10, '10 minutes before'],
  [15, '15 minutes before'],
  [30, '30 minutes before'],
  [60, '1 hour before'],
  [120, '2 hours before'],
  [1440, '1 day before'],
];
const PRESET_VALUES = new Set(PRESET_OFFSETS.map(([value]) => value));
const UNIT_MULTIPLIERS = { minutes: 1, hours: 60, days: 1440 };
export const SOUND_BEHAVIOR_OPTIONS = [
  { value: 'once', label: 'Play once' },
  { value: 'until_dismissed', label: 'Repeat until dismissed' },
  { value: 'duration_15', label: 'Repeat for 15 seconds' },
  { value: 'duration_30', label: 'Repeat for 30 seconds' },
  { value: 'duration_60', label: 'Repeat for 60 seconds' },
];

export function soundBehaviorToUiValue(reminders) {
  return reminders?.soundRepeatMode === 'duration'
    ? `duration_${reminders.soundDurationSeconds}`
    : reminders?.soundRepeatMode || 'once';
}

export function soundBehaviorFromUiValue(value) {
  if (value.startsWith('duration_')) {
    return {
      soundRepeatMode: 'duration',
      soundDurationSeconds: Number(value.replace('duration_', '')),
      soundRepeatExplicit: true,
    };
  }
  return {
    soundRepeatMode: value,
    soundDurationSeconds: null,
    soundRepeatExplicit: true,
  };
}

export function notificationPermissionState() {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  if (window.Notification.permission === 'granted') return 'enabled';
  if (window.Notification.permission === 'denied') return 'blocked';
  return 'not-enabled';
}

function permissionLabel(status) {
  return {
    enabled: 'Enabled',
    blocked: 'Blocked',
    unsupported: 'Unsupported',
    'not-enabled': 'Not enabled',
  }[status];
}

export function convertCustomReminderOffset(amount, unit) {
  const numericAmount = Number(amount);
  const multiplier = UNIT_MULTIPLIERS[unit];
  if (!Number.isInteger(numericAmount) || numericAmount < 1 || !multiplier) return null;
  const offset = numericAmount * multiplier;
  return offset <= 525600 ? offset : null;
}

export function reminderOffsetLabel(offset) {
  if (offset === 0) return 'At due time';
  if (offset % 1440 === 0) {
    const days = offset / 1440;
    return `${days} day${days === 1 ? '' : 's'} before`;
  }
  if (offset % 60 === 0) {
    const hours = offset / 60;
    return `${hours} hour${hours === 1 ? '' : 's'} before`;
  }
  return `${offset} minute${offset === 1 ? '' : 's'} before`;
}

export function TaskReminderSection({
  reminders,
  onChange,
  hasDueDateTime,
}) {
  const [permission, setPermission] = useState(notificationPermissionState);
  const [customAmount, setCustomAmount] = useState('');
  const [customUnit, setCustomUnit] = useState('minutes');
  const offsets = reminders.offsetsMinutes || [];
  const customOffsets = useMemo(
    () => offsets.filter((offset) => !PRESET_VALUES.has(offset)),
    [offsets],
  );

  const patch = (next) => onChange({ ...reminders, ...next });
  const setOffset = (offset, checked) => {
    const next = checked
      ? [...new Set([...offsets, offset])]
      : offsets.filter((value) => value !== offset);
    patch({ offsetsMinutes: next.sort((a, b) => a - b).slice(0, 10) });
  };

  const requestNotificationPermission = async () => {
    if (notificationPermissionState() === 'unsupported') return;
    try {
      const result = await window.Notification.requestPermission();
      setPermission(result === 'granted' ? 'enabled' : result === 'denied' ? 'blocked' : 'not-enabled');
    } catch {
      setPermission(notificationPermissionState());
      toast.error('Browser notification permission could not be requested');
    }
  };

  const testSound = async () => {
    const result = await playReminderSound(reminders.soundType, reminders.soundVolume);
    if (!result.played && result.reason !== 'disabled') {
      toast.error(result.reason === 'unsupported'
        ? 'Web Audio is not supported in this browser'
        : 'The browser blocked audio. Interact with the page and try again.');
    }
  };

  const addCustomOffset = () => {
    const offset = convertCustomReminderOffset(customAmount, customUnit);
    if (offset == null) {
      toast.error('Enter a whole reminder amount within one year');
      return;
    }
    if (!offsets.includes(offset) && offsets.length >= 10) {
      toast.error('A task can have at most 10 reminder times');
      return;
    }
    setOffset(offset, true);
    setCustomAmount('');
  };

  return (
    <section
      className="space-y-4 rounded-xl border border-border bg-muted/20 p-4"
      data-testid="task-reminders-section"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary" />
            <p className="text-sm font-semibold">Task reminders</p>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Receive browser, in-app, and sound reminders while the app is open.
          </p>
        </div>
        <Switch
          checked={reminders.enabled}
          disabled={!hasDueDateTime && !reminders.enabled}
          onCheckedChange={(enabled) => patch({ enabled })}
          aria-label="Enable task reminders"
          data-testid="task-reminders-toggle"
        />
      </div>

      {!hasDueDateTime ? (
        <p className="text-xs text-amber-600 dark:text-amber-400" role="status">
          Add both a due date and due time to enable reminders.
        </p>
      ) : null}

      {reminders.enabled ? (
        <div className="space-y-5 border-t border-border pt-4" data-testid="task-reminder-controls">
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label>Browser notifications</Label>
                <p className="text-xs text-muted-foreground">
                  Permission status: {permissionLabel(permission)}
                </p>
              </div>
              <Switch
                checked={reminders.browserNotification}
                onCheckedChange={(browserNotification) => patch({ browserNotification })}
                aria-label="Use browser notifications"
              />
            </div>
            {permission === 'not-enabled' ? (
              <Button type="button" variant="outline" size="sm" onClick={requestNotificationPermission}>
                Enable Browser Notifications
              </Button>
            ) : null}
            {permission === 'blocked' ? (
              <p className="text-xs text-muted-foreground">
                Notifications are blocked in browser settings. In-app reminders and sound can still work.
              </p>
            ) : null}
          </div>

          <div className="space-y-3 border-t border-border pt-4">
            <div className="flex items-center justify-between gap-3">
              <Label>Reminder sound</Label>
              <Switch
                checked={reminders.sound}
                onCheckedChange={(sound) => patch({ sound })}
                aria-label="Play reminder sound"
              />
            </div>
            {reminders.sound ? (
              <>
                <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                  <Select value={reminders.soundType} onValueChange={(soundType) => patch({ soundType })}>
                    <SelectTrigger aria-label="Reminder sound type" data-testid="task-reminder-sound">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REMINDER_SOUND_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={testSound}
                    disabled={reminders.soundType === 'none'}
                  >
                    <Play className="mr-1.5 h-4 w-4" />
                    Test sound
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label>Sound behavior</Label>
                  <Select
                    value={soundBehaviorToUiValue(reminders)}
                    onValueChange={(value) => patch(soundBehaviorFromUiValue(value))}
                  >
                    <SelectTrigger
                      aria-label="Sound behavior"
                      data-testid="task-reminder-sound-behavior"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SOUND_BEHAVIOR_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Repeated alarms pause briefly between each complete sound pattern.
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <Label htmlFor="task-reminder-volume">Volume</Label>
                    <span>{reminders.soundVolume}%</span>
                  </div>
                  <Slider
                    id="task-reminder-volume"
                    min={0}
                    max={100}
                    step={1}
                    value={[reminders.soundVolume]}
                    onValueChange={([soundVolume]) => patch({ soundVolume })}
                    aria-label="Reminder sound volume"
                    data-testid="task-reminder-volume"
                  />
                </div>
              </>
            ) : null}
          </div>

          <div className="space-y-3 border-t border-border pt-4">
            <Label>Notify me</Label>
            <div className="grid gap-2 sm:grid-cols-2">
              {PRESET_OFFSETS.map(([offset, label]) => (
                <label key={offset} className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm">
                  <Checkbox
                    checked={offsets.includes(offset)}
                    onCheckedChange={(checked) => setOffset(offset, checked === true)}
                    aria-label={label}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-3 border-t border-border pt-4">
            <Label>Custom reminder</Label>
            <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
              <Input
                type="number"
                min={1}
                step={1}
                value={customAmount}
                onChange={(event) => setCustomAmount(event.target.value)}
                placeholder="Amount"
                aria-label="Custom reminder amount"
              />
              <Select value={customUnit} onValueChange={setCustomUnit}>
                <SelectTrigger aria-label="Custom reminder unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="minutes">Minutes</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                  <SelectItem value="days">Days</SelectItem>
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" onClick={addCustomOffset}>Add</Button>
            </div>
            {customOffsets.length ? (
              <div className="flex flex-wrap gap-2">
                {customOffsets.map((offset) => (
                  <Badge key={offset} variant="secondary" className="gap-1">
                    {reminderOffsetLabel(offset)}
                    <button
                      type="button"
                      aria-label={`Remove ${reminderOffsetLabel(offset)}`}
                      onClick={() => setOffset(offset, false)}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default TaskReminderSection;
